// PlayFirst Parent — Therapist link requests card.
// Shows on home if there's a pending or active therapist link.
import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Stethoscope, Check, X, Loader2 } from 'lucide-react';
import { PFP } from './styles';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export default function TherapistLinksCard({ children = [] }) {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/parent-app/therapist-links`, auth());
        setLinks(r.data?.links || []);
      } catch { /* silently swallow */ }
      finally { setLoading(false); }
    })();
  }, []);

  const respond = async (link, accept) => {
    setActing(link.id);
    try {
      const body = { accept };
      // If therapist didn't specify a child and we have multiple, pick the first.
      if (accept && !link.child_id) {
        if (children.length === 0) {
          toast.error('Add a child profile before accepting a therapist link.');
          setActing(null);
          return;
        }
        body.child_id = children[0].id;
      }
      const r = await axios.post(`${API}/parent-app/therapist-links/${link.id}/respond`, body, auth());
      setLinks((prev) => prev.map((l) =>
        l.id === link.id ? { ...l, status: r.data.status } : l
      ));
      toast.success(accept ? `Linked with ${link.therapist_email}` : 'Declined');

      // Sep 2026 — refresh user capabilities so the unified 2-tab
      // /parent-home shell unlocks WITHOUT a logout/login. Non-fatal
      // if the refresh fails — parent will pick up caps on next login.
      if (accept) {
        try {
          const me = await axios.get(`${API}/auth/me`, auth());
          if (me?.data) {
            localStorage.setItem('user', JSON.stringify(me.data));
            if (me.data.capabilities?.clinical_parent && me.data.capabilities?.pfp) {
              // Give the toast a beat, then whisk them to the unified home.
              setTimeout(() => navigate('/parent-home'), 900);
            }
          }
        } catch { /* silent */ }
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not respond');
    } finally {
      setActing(null);
    }
  };

  const pending = links.filter((l) => l.status === 'pending');
  // Dedupe active rows by therapist_id so a therapist linked twice
  // shows once. (Bug: server previously created duplicate rows across
  // the parent/parent_self twin identity — fixed on the create side
  // in iter 95 but existing rows still exist in prod.)
  const activeMap = new Map();
  for (const l of links) {
    if (l.status !== 'active') continue;
    const key = l.therapist_id || l.therapist_email || l.id;
    if (!activeMap.has(key)) activeMap.set(key, l);
  }
  const active = Array.from(activeMap.values());
  if (loading) return null;
  if (pending.length === 0 && active.length === 0) return null;

  return (
    <>
      {pending.map((l) => (
        <motion.section
          key={l.id}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`${PFP.card} p-5 border-l-4 border-l-[#7C6F9C]`}
          data-testid={`therapist-link-pending-${l.id}`}
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-2xl bg-[#7C6F9C]/10 text-[#7C6F9C] flex items-center justify-center flex-shrink-0">
              <Stethoscope className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-wider text-[#7C6F9C] font-medium">Link request</p>
              <h3 className="font-serif text-lg text-[#3a2f4f] mt-0.5">
                {l.therapist_name || l.therapist_email}
              </h3>
              <p className="text-sm text-stone-600 mt-1 leading-relaxed">
                They&apos;d like to view {l.child_name ? `${l.child_name}'s` : "your child's"} Language Map (read-only)
                and occasionally send you a tailored play idea. You can revoke this anytime.
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                <button
                  onClick={() => respond(l, true)}
                  disabled={acting === l.id}
                  className={PFP.btnPrimary + ' inline-flex items-center gap-1.5 text-sm py-2'}
                  data-testid={`therapist-link-accept-${l.id}`}
                >
                  {acting === l.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  Accept
                </button>
                <button
                  onClick={() => respond(l, false)}
                  disabled={acting === l.id}
                  className={PFP.btnGhost + ' inline-flex items-center gap-1.5 text-sm py-2'}
                  data-testid={`therapist-link-decline-${l.id}`}
                >
                  <X className="w-4 h-4" />
                  Decline
                </button>
              </div>
            </div>
          </div>
        </motion.section>
      ))}
      {active.length > 0 && (
        <motion.section
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`${PFP.card} p-4 bg-stone-50/60`}
          data-testid="therapist-links-active"
        >
          <div className="flex items-center gap-3">
            <Stethoscope className="w-4 h-4 text-stone-500" />
            <p className="text-sm text-stone-600">
              Linked with {active.length === 1
                ? (active[0].therapist_name || active[0].therapist_email)
                : active.map((a) => a.therapist_name || a.therapist_email).join(', ')}.
              Ideas they share will appear in your feed.
            </p>
          </div>
        </motion.section>
      )}
    </>
  );
}
